import { useQueryClient } from "@tanstack/react-query";

import { type InvalidateQueryOptions, QueryModule, invalidateQueries } from "@/data/invalidateQueries";
import { useAuthGuardedMutation } from "@/hooks/useAuthGuardedMutation";
import { useAuthGuardedQuery } from "@/hooks/useAuthGuardedQuery";
import type { AppMutationOptions, AppQueryOptions } from "@/types/react-query";

import { BizShortlistApi } from "./bizShortlist.api";
import type { BizShortlistModels } from "./bizShortlist.models";

export namespace BizShortlistQueries {
  export const moduleName = QueryModule.BizShortlist;

  export const keys = {
    all: [moduleName] as const,
    getShortlist: () => [...keys.all, "/biz/shortlist"] as const,
  };

  /**
   * Query `useGetShortlist`
   * @summary Get current user shortlist
   * @param { AppQueryOptions } options Query options
   * @returns { UseQueryResult<BizShortlistModels.BizShortlistResponseDto> }
   * @statusCodes [200]
   */
  export const useGetShortlist = <TData>(options?: AppQueryOptions<typeof BizShortlistApi.getShortlist, TData>) => {
    return useAuthGuardedQuery({
      queryKey: keys.getShortlist(),
      queryFn: BizShortlistApi.getShortlist,
      ...options,
    });
  };

  /**
   * Mutation `useAddPosition`
   * @summary Add a position to shortlist
   * @param { BizShortlistModels.AddPositionToShortlistDto } mutation.data Body parameter
   * @param { AppMutationOptions & InvalidateQueryOptions } options Mutation options
   * @returns { UseMutationResult<BizShortlistModels.BizShortlistResponseDto> }
   * @statusCodes [200, 201]
   */
  export const useAddPosition = (
    options?: AppMutationOptions<
      typeof BizShortlistApi.addPosition,
      { data: BizShortlistModels.AddPositionToShortlistDto }
    > &
      InvalidateQueryOptions,
  ) => {
    const queryClient = useQueryClient();

    return useAuthGuardedMutation({
      mutationFn: ({ data }) => BizShortlistApi.addPosition(data),
      ...options,
      onSuccess: (...args) => {
        invalidateQueries(queryClient, moduleName, options);
        options?.onSuccess?.(...args);
      },
    });
  };

  /**
   * Mutation `useRemovePosition`
   * @summary Remove a position from shortlist
   * @param { string } mutation.positionCode Path parameter
   * @param { AppMutationOptions & InvalidateQueryOptions } options Mutation options
   * @returns { UseMutationResult<BizShortlistModels.BizShortlistResponseDto> }
   * @statusCodes [200]
   */
  export const useRemovePosition = (
    options?: AppMutationOptions<typeof BizShortlistApi.removePosition, { positionCode: string }> &
      InvalidateQueryOptions,
  ) => {
    const queryClient = useQueryClient();

    return useAuthGuardedMutation({
      mutationFn: ({ positionCode }) => BizShortlistApi.removePosition(positionCode),
      ...options,
      onSuccess: (...args) => {
        invalidateQueries(queryClient, moduleName, options);
        options?.onSuccess?.(...args);
      },
    });
  };
}
